import { Shield, Clock, CircleDashed } from "lucide-react";
import type { ComplianceEntry } from "@/api/client";

interface ComplianceBadgeProps {
  compliance: ComplianceEntry;
}

interface BadgeConfig {
  icon: React.ComponentType<{ className?: string }>;
  bgClass: string;
  borderClass: string;
  textClass: string;
  iconClass: string;
  label: string;
}

const STATUS_CONFIG: Record<string, BadgeConfig> = {
  certified: {
    icon: Shield,
    bgClass: "bg-emerald-50",
    borderClass: "border-emerald-200",
    textClass: "text-emerald-800",
    iconClass: "text-emerald-500",
    label: "Certified",
  },
  in_progress: {
    icon: Clock,
    bgClass: "bg-blue-50",
    borderClass: "border-blue-200",
    textClass: "text-blue-800",
    iconClass: "text-blue-500",
    label: "In Progress",
  },
  not_started: {
    icon: CircleDashed,
    bgClass: "bg-gray-50",
    borderClass: "border-gray-200",
    textClass: "text-gray-600",
    iconClass: "text-gray-400",
    label: "Planned",
  },
};

const DEFAULT_CONFIG: BadgeConfig = {
  icon: CircleDashed,
  bgClass: "bg-gray-50",
  borderClass: "border-gray-200",
  textClass: "text-gray-600",
  iconClass: "text-gray-400",
  label: "Unknown",
};

function formatDate(dateStr: string | null): string | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function ComplianceBadge({ compliance }: ComplianceBadgeProps) {
  const config = STATUS_CONFIG[compliance.status] ?? DEFAULT_CONFIG;
  const Icon = config.icon;
  const validUntil = formatDate(compliance.valid_until);

  return (
    <div
      className={`rounded-lg border p-4 ${config.bgClass} ${config.borderClass}`}
    >
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 flex-shrink-0 ${config.iconClass}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className={`text-sm font-semibold ${config.textClass}`}>
            {compliance.framework_name}
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">
            {compliance.framework_code}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${config.bgClass} ${config.textClass}`}
            >
              {config.label}
            </span>
            {validUntil && compliance.status === "certified" && (
              <span className="text-xs text-gray-400">
                Valid until {validUntil}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
